import { autocompletion } from "@codemirror/autocomplete";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowRight,
  faBuilding,
  faCalculatorSimple,
  faDatabase,
  faInfoCircle,
  faPlay,
  faPlus,
  faTrashAlt,
  faSearch,
  faSync,
  faWarning,
  faCube,
} from "@fortawesome/pro-regular-svg-icons";
import { faPlay as faPlaySolid } from "@fortawesome/pro-solid-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosError } from "axios";
import { subMinutes } from "date-fns";
import { AnimatePresence, m } from "framer-motion";
import { capitalize, isEmpty, isString, noop, uniq } from "lodash";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  Controller,
  ControllerRenderProps,
  FormProvider,
  useController,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";
import * as yup from "yup";

import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { CodeInput } from "src/base-components/CodeInput/CodeInput";
import { EditorBoundsProvider } from "src/base-components/CodeInput/useEditorBounds";
import { Combobox } from "src/base-components/Combobox";
import { DataList } from "src/base-components/DataList";
import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { Select, type Option } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import { Tabs } from "src/base-components/Tabs";
import {
  FeatureMappingInput as FeatureMappingT,
  EntityMapping,
  FeatureQueryStatus,
  PreviewQueryResponse,
  FeatureQueryCreate,
  FeatureQueryPut,
  PreviewQueryRequestEnvironmentEnum,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { Modal, useModal } from "src/design-system/Modal";
import { TAKTILE_TEAM_NOTIFIED } from "src/design-system/Toast/constants";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { getSchemaIcon } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { ManageFeatureModal } from "src/featureCatalogue/Features/ManageFeatureModal";
import {
  inferDataTypes,
  SQLPreviewTable,
} from "src/featureCatalogue/Queries/SQLResonsePreviewTable";
import {
  useCreateFeatureQuery,
  useExploreEventTable,
  UseFeatureQueryPreview,
  useFeatureQueryPreview,
  useFeatures,
  useUpdateFeatureQuery,
} from "src/featureCatalogue/queries";
import { formatTimeWindow, Query } from "src/featureCatalogue/utils";
import { queryClient } from "src/queryClient";
import { isFeatureFlagEnabled, FEATURE_FLAGS } from "src/router/featureFlags";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { TYPE_ICONS } from "src/utils/constants";
import { getTypeIcon } from "src/utils/icons";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";
import {
  objectToSpeakPythonString,
  speakPythonPrimitive,
} from "src/utils/speakPython";
import { errorMessage } from "src/utils/stringUtils";

type ManagedQuery = Query | Omit<Query, "id"> | undefined;

type ManageQueryModalProps = {
  open: boolean;
  onClose: () => void;
  query?: ManagedQuery;
  afterLeave?: () => void;
  readOnly?: boolean;
};

const getInitialQueryValue = (query: ManagedQuery): ManagedQuery => {
  if (!query?.window_hop) {
    return query;
  }
  return {
    ...query,
    window_hop: query.window_hop < 60 ? 60 : query.window_hop,
  };
};

type ColumnExistsValidationContext = {
  availableColumns: string[];
};

const columnExistsValidation = {
  name: "column-exists",
  message: "Column name doesn't exist in query results. Update the value.",
  test: (
    value: string | undefined,
    context: yup.TestContext<yup.AnyObject>,
  ) => {
    if (!value) return true;
    return context.options.context?.availableColumns?.includes(value) ?? false;
  },
} as const;

const COMPLETION_EXTENSION = autocompletion({
  override: [() => null],
});

const KEY_BINDINGS = [
  {
    mac: "Cmd-Enter",
    win: "Ctrl-Enter",
    linux: "Ctrl-Enter",
    run: () => {
      return true;
    },
  },
];

const schema = yup.object({
  query: yup.string().required().min(1),
  columns: yup
    .object({
      entities: yup
        .array()
        .of(
          yup.object().shape(
            {
              column_name: yup.string().when("entity_name", {
                is: (entity_name: string) => Boolean(entity_name),
                then: (schema) =>
                  schema
                    .required("Column name is required when entity is selected")
                    .test(columnExistsValidation),
                otherwise: (schema) => schema.nullable(),
              }),
              entity_name: yup.string().when("column_name", {
                is: (column_name: string) => Boolean(column_name),
                then: (schema) =>
                  schema.required("Entity is required when column is selected"),
                otherwise: (schema) => schema.nullable(),
              }),
            },
            [["column_name", "entity_name"]],
          ),
        )
        .length(1),
      features: yup
        .array()
        .of(
          yup.object().shape(
            {
              column_name: yup.string().when("feature_key", {
                is: (feature_key: string) => Boolean(feature_key),
                then: (schema) =>
                  schema
                    .transform((value) => value?.trim())
                    .required(
                      "Column name is required when feature is selected",
                    )
                    .test(columnExistsValidation),
                otherwise: (schema) => schema.nullable(),
              }),
              feature_key: yup.string().when("column_name", {
                is: (column_name: string) => Boolean(column_name),
                then: (schema) =>
                  schema.required(
                    "Feature is required when column is selected",
                  ),
                otherwise: (schema) => schema.nullable(),
              }),
              feature_type: yup.string().optional(),
              feature_default: yup
                .string()
                .nullable()
                .test(
                  "is-required",
                  "Default value is required, set None if not applicable",
                  (value) => Boolean(value),
                ),
            },
            [["column_name", "feature_key"]],
          ),
        )
        .min(1)
        .test(
          "at-least-one-mapping",
          "At least one feature mapping must be complete",
          (features) => {
            return features?.some(
              (feature) => feature.column_name && feature.feature_key,
            );
          },
        ),
      grouping_keys: yup
        .array()
        .of(yup.string().test(columnExistsValidation).nullable())
        .max(1),
    })
    .required()
    .test(
      "use-of-all-available-columns",
      "Mapping must use all available columns. Preview data doesn't match the mapping configuration.",
      (value, { options }) => {
        // Access context values passed to the resolver
        const availableColumns = uniq(options.context?.availableColumns);

        const usedColumns = uniq(
          [
            ...(value.entities?.map((entity) => entity.column_name) ?? []),
            ...(value.features?.map((feature) => feature.column_name) ?? []),
            ...(value.grouping_keys ?? []),
          ].filter(Boolean),
        );

        return (
          usedColumns.length === availableColumns.length &&
          usedColumns.every((column) => availableColumns.includes(column))
        );
      },
    ),
  window_length: yup.number().required(),
  window_hop: yup.number().required(),
  end_datetime: yup.date().optional(),
});

type QueryFormValues = yup.InferType<typeof schema>;

const defaultFormValues = {
  status: FeatureQueryStatus.INACTIVE,
  query: "",
  columns: {
    entities: [{ column_name: "", entity_name: "" }],
    grouping_keys: [],
    features: [
      {
        column_name: "",
        feature_key: "",
        feature_type: "",
        feature_default: "None",
      },
    ],
  },
};

const getCompletionExtension = (isDisabled: boolean) => {
  // If disabled, return empty array to prevent setting up autocompletion
  if (isDisabled) {
    return [];
  }
  return COMPLETION_EXTENSION;
};

const VALUE_ERROR_PREFIX = "Value error, ";

const parseFeatureDefaultError = (error: unknown) => {
  if (error instanceof AxiosError && error?.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (Array.isArray(detail)) {
      const errorMsg = detail.map((item) => {
        if (item.type === "value_error") {
          return `Wrong type for input ${item.input}: ${item.msg.replace(VALUE_ERROR_PREFIX, "")}.`;
        }
        return item.msg;
      });
      return errorMsg.join(" ");
    }
    return detail;
  }

  return TAKTILE_TEAM_NOTIFIED;
};

enum TabKeys {
  SQL_RESPONSE = "sql-response",
  EXPLORE_TABLES = "explore-tables",
}

const SectionHeader: React.FC<{
  title: string;
  description?: string;
  tooltip?: React.ReactNode;
}> = ({ title, description, tooltip }) => {
  return (
    <div className="pb-2">
      <h3
        className={twJoin(
          "text-gray-800 font-inter-semibold-13px",
          description && "pb-2",
        )}
      >
        <span>{title}</span>
        {tooltip && (
          <Tooltip body={tooltip}>
            <Icon color="text-gray-800" icon={faInfoCircle} size="2xs" />
          </Tooltip>
        )}
      </h3>
      {description && (
        <p className="text-gray-500 font-inter-normal-12px">{description}</p>
      )}
    </div>
  );
};

export const ManageQueryModal: React.FC<ManageQueryModalProps> = ({
  open,
  onClose,
  query,
  afterLeave,
  readOnly = false,
}) => {
  const isEditing = query && "id" in query;
  const featureQueryPreview = useFeatureQueryPreview();
  const featuresQuery = useFeatures();

  const form = useForm<QueryFormValues>({
    reValidateMode: "onBlur",
    values: getInitialQueryValue(query),
    defaultValues: defaultFormValues,
    disabled: readOnly,
    resolver: async (...args) => {
      // Ignore context, we pass it in manually
      const [data, _, options] = args;

      const response: PreviewQueryResponse["data"] | undefined =
        featureQueryPreview.previewData?.data;

      // Make sure we have the latest preview data for validation
      if (
        featureQueryPreview.variables &&
        data.query !== featureQueryPreview.variables.queryString &&
        data.query.length > 0
      ) {
        await featureQueryPreview.triggerPreview({
          queryString: data.query,
          windowLength: form.getValues("window_length"),
          endTime: form.getValues("end_datetime"),
          environment: featureQueryPreview.variables.environment,
        });
      }

      return yupResolver(schema)(
        data,
        {
          availableColumns: Object.keys(response?.at(0) ?? {}),
        } satisfies ColumnExistsValidationContext,
        options,
      );
    },
  });

  const createFeatureQueryMutation = useCreateFeatureQuery();
  const updateFeatureQueryMutation = useUpdateFeatureQuery();

  const handleSubmit = async (data: QueryFormValues) => {
    try {
      // Make sure we have the latest preview data for submitting
      if (
        featureQueryPreview.variables &&
        data.query !== featureQueryPreview.variables?.queryString &&
        data.query.length > 0
      ) {
        await featureQueryPreview.triggerPreview({
          queryString: data.query,
          windowLength: form.getValues("window_length"),
          endTime: form.getValues("end_datetime"),
          environment: featureQueryPreview.variables.environment,
        });
      }

      const featureQuery = prepareFeatureQuery(
        data,
        featureQueryPreview.previewData?.data ?? [],
      );

      if (isEditing) {
        await updateFeatureQueryMutation.mutateAsync({
          id: query.id,
          ...featureQuery,
          etag: query.etag,
        });
      } else {
        await createFeatureQueryMutation.mutateAsync(featureQuery);
      }

      onClose();
    } catch (error) {
      logger.error(error);

      if (isPreconditionError(error)) {
        await queryClient.invalidateQueries({ queryKey: ["feature-queries"] });
        if (isEditing) {
          await queryClient.invalidateQueries({
            queryKey: ["feature-query", query.id],
          });
        }
        toastActions.failure({
          title: `Failed to ${isEditing ? "update" : "create"} Feature query`,
          description:
            "Somebody has changed the Feature query, check the updated configuration and try again",
        });
      } else {
        toastActions.failure({
          title: `Failed to ${isEditing ? "update" : "create"} Feature query`,
          description: parseFeatureDefaultError(error),
        });
      }
    }
  };

  const windowLength = form.watch("window_length");
  const windowHop = form.watch("window_hop");

  const refreshIntervalOptions = useMemo(() => {
    return getWindowHopOptions().map((option) => ({
      ...option,
      disabled: isWindowHopOptionDisabled(parseInt(option.key), windowLength)
        ? "This option is not available for the selected time window"
        : false,
    }));
  }, [windowLength]);
  const onChangeWindowLength = useCallback(
    (
      value: string,
      field: ControllerRenderProps<QueryFormValues, "window_length">,
    ) => {
      const parsedWindowLengthValue = parseInt(value);

      if (isWindowHopOptionDisabled(windowHop, parsedWindowLengthValue)) {
        form.resetField("window_hop", {
          // @ts-expect-error - we want to reset the field to null
          defaultValue: null,
        });
      }

      return field.onChange(parsedWindowLengthValue);
    },
    [form, windowHop],
  );

  return (
    <Modal
      afterLeave={() => {
        afterLeave?.();
        form.reset();
        featureQueryPreview.reset();
      }}
      autoFocus={false}
      closeMode="persistent"
      open={open}
      size="xl"
      onClose={onClose}
    >
      <Modal.Header>
        <div className="flex justify-between">
          <div>{isEditing ? "Edit Feature query" : "Create Feature query"}</div>
          {readOnly && (
            <Pill variant="gray">
              <Pill.Icon icon={faSync} />
              <Pill.Text>
                You cannot edit a query that has been activated on Live
              </Pill.Text>
            </Pill>
          )}
        </div>
      </Modal.Header>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FormProvider {...form}>
          <Modal.Content>
            <div className="flex h-[calc((100vh-240px)*0.8)] min-h-[585px] shrink-0 gap-x-5 overflow-visible pb-5">
              <div className="flex min-w-[520px] flex-1 flex-col">
                <SectionHeader
                  title="SQL Query"
                  tooltip={
                    <div className="font-inter-normal-12px">
                      <p>
                        Write a SQL query to calculate features. The SQL query
                        must
                      </p>
                      <ul className="marker:mr-1">
                        <li className="pl-2 marker:mr-1">
                          &bull; include exactly one time-based BETWEEN ? AND ?
                          clause.
                        </li>
                        <li className="pl-2">
                          &bull; not include * wildcards or data modification
                          statements.
                        </li>
                        <li className="pl-2">
                          &bull; return feature values by entity ID and/or
                          grouping columns.
                        </li>
                      </ul>
                    </div>
                  }
                />
                <div className="min-h-[18rem] grow pb-6 pt-2">
                  <Controller
                    name="query"
                    render={({ field, fieldState }) => (
                      <CodeEditor
                        // Use the conditional completion extension
                        completionExtension={getCompletionExtension(readOnly)}
                        dataLoc="sql-query-editor"
                        disabled={readOnly}
                        errorHighlightedLine={
                          fieldState.error?.message ? 1 : undefined
                        }
                        keyBindings={KEY_BINDINGS}
                        language="sql"
                        value={field.value}
                        // Revalidate the form when the query changes
                        onBlur={() =>
                          form.formState.isSubmitted && form.trigger()
                        }
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>

                <QueryHasChangedCallout previewMutation={featureQueryPreview} />

                <div className="flex items-center gap-x-4 pb-4">
                  <div className="flex-1">
                    <SectionHeader
                      title="Time window"
                      tooltip="The time window for calculating your feature (e.g., last 30 days of transactions). Maximum 365 days."
                    />
                    <Controller
                      control={form.control}
                      name="window_length"
                      render={({ field }) => (
                        <Select
                          dataLoc="window-length-select"
                          disabled={readOnly}
                          errored={!!form.formState.errors.window_length}
                          options={getWindowLengthOptions()}
                          placeholder="Select time window"
                          value={field.value ? field.value.toString() : null}
                          searchable
                          onChange={(value) =>
                            onChangeWindowLength(value, field)
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <SectionHeader title="End date and time for preview" />
                    <Controller
                      control={form.control}
                      defaultValue={subMinutes(new Date(), 1)}
                      name="end_datetime"
                      render={({ field }) => (
                        <DateTimePickerDropdown
                          disabled={readOnly}
                          errored={!!form.formState.errors.end_datetime}
                          placeholder="Select end date and time"
                          value={field.value as Date | undefined}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="pb-6">
                  <SectionHeader
                    title="Refresh interval"
                    tooltip="How often your feature values are updated. Shorter intervals mean more recent data. Match to your decision-making needs (e.g., 1 min for real-time fraud, 1 day for periodic reviews)"
                  />

                  <Controller
                    control={form.control}
                    name="window_hop"
                    render={({ field }) => {
                      return (
                        <Select
                          dataLoc="window-hop-select"
                          disabled={readOnly}
                          errored={!!form.formState.errors.window_hop}
                          options={refreshIntervalOptions}
                          placeholder="Select refresh interval"
                          value={field.value ? field.value.toString() : null}
                          searchable
                          onChange={(value) => field.onChange(parseInt(value))}
                        />
                      );
                    }}
                  />
                </div>
                <div className="flex items-start gap-x-2">
                  <RunQueryButton
                    disabled={readOnly}
                    featurePreview={featureQueryPreview}
                  />
                </div>
              </div>
              <div className="min-h-0 flex-1">
                <FeatureQueryPreview previewMutation={featureQueryPreview} />
              </div>
            </div>
            <div className="min-h-0 w-full border-t border-t-gray-200 pt-6">
              <SectionHeader
                description="Map your SQL query results to entities and features for use in decision flows"
                title="Mapping"
              />
              {form.formState.errors.columns?.root?.message && (
                <div className="pb-4">
                  <Callout type="error">
                    {form.formState.errors.columns?.root?.message}
                  </Callout>
                </div>
              )}
              <div className="flex flex-col items-stretch gap-y-4 pb-6">
                {(query && "id" in query) ||
                (featureQueryPreview.previewData?.data &&
                  !featureQueryPreview.error) ? (
                  <MappingConfiguration
                    disabled={readOnly}
                    featuresQuery={featuresQuery}
                    previewData={featureQueryPreview.previewData?.data ?? []}
                    previewMutation={featureQueryPreview}
                  />
                ) : (
                  <Callout icon={faInfoCircle} type="info">
                    Run query to get column name suggestions and map them to
                    Entities or Features
                  </Callout>
                )}
              </div>
            </div>
          </Modal.Content>
          <Modal.Footer
            primaryButton={
              <Button
                dataLoc="sql-modal-done"
                disabled={readOnly}
                htmlType="submit"
                loading={form.formState.isSubmitting}
                variant="primary"
              >
                Done
              </Button>
            }
          />
        </FormProvider>
      </form>
    </Modal>
  );
};

const FeatureQueryPreview: React.FC<{
  previewMutation: UseFeatureQueryPreview;
}> = ({ previewMutation }) => {
  const [activeTab, setActiveTab] = useState<TabKeys>(TabKeys.SQL_RESPONSE);

  useEffect(() => {
    if (previewMutation.isPending || previewMutation.isSuccess) {
      setActiveTab(TabKeys.SQL_RESPONSE);
    }
  }, [previewMutation.isPending, previewMutation.isSuccess]);

  return (
    <Tabs
      containerClassName="h-full min-h-0 flex flex-col"
      panelClassName="h-full"
      panelsClassName="flex-1 min-h-0"
      selectedKey={activeTab}
      tabListClassName="border-b border-gray-200 mb-6 -top-5 z-10"
      tabs={[
        {
          key: TabKeys.SQL_RESPONSE,
          label: "SQL response",
          content: <SQLResponsePreview featurePreview={previewMutation} />,
        },
        {
          key: TabKeys.EXPLORE_TABLES,
          label: "Explore",
          content: <ExploreTables />,
        },
      ]}
      onChange={(key) => setActiveTab(key as TabKeys)}
    />
  );
};

const RunQueryButton: React.FC<{
  featurePreview: UseFeatureQueryPreview;
  disabled?: boolean;
}> = ({ featurePreview, disabled }) => {
  const [triggerForm, setTriggerForm] = useState(false);
  const { getValues, trigger } = useFormContext<QueryFormValues>();

  const queryString = useWatch({ name: "query" });

  // Triggering form via useEffect so that the form resolver has the latest data available
  useEffect(() => {
    if (triggerForm) {
      setTimeout(() => trigger());
      setTriggerForm(false);
    }
  }, [triggerForm, trigger]);

  const handleRunQuery = (environment: PreviewQueryRequestEnvironmentEnum) => {
    try {
      const [queryString, windowLength, endTime] = getValues([
        "query",
        "window_length",
        "end_datetime",
      ]);

      if (!queryString.length) return;

      featurePreview.triggerPreview({
        queryString,
        windowLength,
        environment,
        endTime,
      });

      featurePreview.promise.current?.promise.then(() => {
        setTriggerForm(true);
      });
    } catch (error) {
      logger.error(error);
    }
  };

  const isLiveQueryRunning =
    featurePreview.isPending &&
    featurePreview.variables?.environment ===
      PreviewQueryRequestEnvironmentEnum.LIVE;
  const isSandboxQueryRunning =
    featurePreview.isPending &&
    featurePreview.variables?.environment ===
      PreviewQueryRequestEnvironmentEnum.SANDBOX;

  return (
    <div className="flex w-full justify-end gap-x-4">
      <Button
        dataLoc="run-query-button-sandbox"
        disabled={!queryString.length || disabled || isLiveQueryRunning}
        iconLeft={faPlaySolid}
        loading={isSandboxQueryRunning}
        variant="secondary"
        onClick={() => {
          if (!featurePreview.isPending) {
            handleRunQuery(PreviewQueryRequestEnvironmentEnum.SANDBOX);
          }
        }}
      >
        Run Query in Sandbox
      </Button>
      <Button
        dataLoc="run-query-button-live"
        disabled={!queryString.length || disabled || isSandboxQueryRunning}
        iconLeft={faPlaySolid}
        loading={isLiveQueryRunning}
        variant="secondary"
        onClick={() => {
          if (!featurePreview.isPending) {
            handleRunQuery(PreviewQueryRequestEnvironmentEnum.LIVE);
          }
        }}
      >
        Run Query in Live
      </Button>
    </div>
  );
};

const getMinWindowHopFromWindowLength = (windowLength: number) => {
  const ONE_HOUR = 3600;
  const ONE_DAY = 86400;
  const SIXTY_DAYS = 5184000;

  const FIFTEEN_SECONDS = 15;
  const ONE_MINUTE_IN_SECONDS = 60;
  const ONE_HOUR_IN_SECONDS = 3600;
  const ONE_DAY_IN_SECONDS = 86400;

  switch (true) {
    case windowLength < ONE_HOUR:
      return FIFTEEN_SECONDS;
    case windowLength < ONE_DAY:
      return ONE_MINUTE_IN_SECONDS;
    case windowLength < SIXTY_DAYS:
      return ONE_HOUR_IN_SECONDS;
    default:
      return ONE_DAY_IN_SECONDS;
  }
};

const getWindowHopOptions = () => {
  const isPowerToolsEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.powertools);

  const allOptions = [
    60, 120, 180, 240, 300, 360, 480, 540, 600, 720, 900, 960, 1080, 1200, 1440,
    1800, 1920, 2160, 2400, 2700, 2880, 3600, 4320, 4800, 5400, 5760, 7200,
    8640, 9600, 10800, 14400, 17280, 21600, 28800, 43200, 86400,
  ];

  // Start from 60 if no limits flag is enabled, otherwise start from 300
  const startValue = isPowerToolsEnabled ? 60 : 300;
  const filteredOptions = allOptions.filter((value) => value >= startValue);

  return filteredOptions.map((duration) => ({
    key: duration.toString(),
    value: formatTimeWindow(duration),
  }));
};

const getWindowLengthOptions = () => {
  const isPowerToolsEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.powertools);

  const allOptions = [
    60, // 1m
    120, // 2m
    240, // 4m
    300, // 5m
    600, // 10m
    1200, // 20m
    1800, // 30m
    3600, // 60m
    5400, // 90m
    7200, // 2h
    14400, // 4h
    21600, // 6h
    43200, // 12h
    86400, // 1d
    172800, // 2d
    345600, // 4d
    604800, // 1w
    1209600, // 14d
    2592000, // 30d
    5184000, // 60d
    7776000, // 90d
    15552000, // 180d
    31536000, // 365d
  ];

  // Start from 300 (5 minutes) if no limits flag is enabled, otherwise allow all options
  const startValue = isPowerToolsEnabled ? 30 : 300;
  const filteredOptions = allOptions.filter((value) => value >= startValue);

  return filteredOptions.map((duration) => ({
    key: duration.toString(),
    value: formatTimeWindow(duration),
  }));
};

const SQLResponsePreview: React.FC<{
  featurePreview: UseFeatureQueryPreview;
}> = ({ featurePreview }) => {
  const isServerError = featurePreview.error?.response?.status === 500;
  const errorTitle = isServerError
    ? "We have received an unexpected error"
    : "Syntax error";

  const errorDetail = featurePreview.error?.response?.data.detail
    ? errorMessage(featurePreview.error.response.data.detail)
    : "Our team has been notified";

  if (!featurePreview.error && featurePreview.previewData?.data?.length === 0) {
    return (
      <div>
        <Callout icon={faWarning} type="error">
          No data found for the selected time range.
          <br />
          Please adjust the end date and time or choose a different time window,
          and try running the query again.
        </Callout>
      </div>
    );
  }

  return (
    <div className="decideScrollbar h-full overflow-hidden rounded-lg border border-gray-200">
      {featurePreview.isIdle && (
        <EmptyState
          description="SQL response will help you map the columns with the input schema"
          headline="Run Query to see SQL response"
          icon={faPlay}
        />
      )}
      {featurePreview.error && (
        <EmptyState
          description={errorDetail}
          headline={errorTitle}
          icon={faDatabase}
          variant="error"
        />
      )}
      {!featurePreview.error &&
        (featurePreview.previewData || featurePreview.isPending) && (
          <SQLPreviewTable
            dataLoc={
              featurePreview.isPending
                ? "sql-preview-table-loading"
                : "sql-preview-table"
            }
            isLoading={featurePreview.isPending}
            rows={featurePreview.previewData?.data}
          />
        )}
    </div>
  );
};

const FeatureMapping: React.FC<{
  columnsOptions: { key: string; value: React.ReactNode }[];
  featureOptions: { key: string; feature_type?: string }[];
  featuresLoading: boolean;
  previewMutation: UseFeatureQueryPreview;
  disabled?: boolean;
}> = ({
  columnsOptions,
  featureOptions,
  featuresLoading,
  previewMutation,
  disabled,
}) => {
  const {
    openModal,
    closeModal,
    isOpen,
    afterLeave,
    data: modalData,
  } = useModal<{ index: number }>();
  const codeInputContainerRef = useRef<HTMLDivElement>(null);
  const { fields, append, remove } = useFieldArray({
    name: "columns.features",
  });

  const form = useFormContext<QueryFormValues>();

  const handleFeatureSelection = useCallback(
    (
      value: Nullable<string>,
      index: number,
      fieldOnChange: (value: Nullable<string>) => void,
    ) => {
      fieldOnChange(value);
      const selectedFeature = featureOptions.find(
        (option) => option.key === value,
      );
      if (
        selectedFeature?.feature_type ===
          PropertyDefinitionOutputTypeEnum.DATE ||
        selectedFeature?.feature_type ===
          PropertyDefinitionOutputTypeEnum.DATETIME
      ) {
        form.setValue(`columns.features.${index}.feature_default`, "None");
      }
    },
    [featureOptions, form],
  );

  const maxX = codeInputContainerRef.current
    ? codeInputContainerRef.current.getBoundingClientRect().x +
      codeInputContainerRef.current.clientWidth
    : null;

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-3">
      <div
        ref={codeInputContainerRef}
        className="flex items-center gap-x-1 text-gray-800 font-inter-medium-12px"
      >
        <Icon color="text-gray-800" icon={faCalculatorSimple} size="xs" />
        <span>Feature mapping</span>
        <Tooltip
          body="Choose the column from your query results that contains the feature value(s)."
          footerAction={{
            onClick: noop,
            text: "Learn more",
          }}
          placement="right-start"
          asChild
        >
          <Icon color="text-gray-500" icon={faInfoCircle} size="2xs" />
        </Tooltip>
      </div>
      <div className="grid grid-cols-[1fr_20px_1fr_98px_20px] gap-2 text-gray-500 font-inter-normal-12px">
        <div>Select column</div>
        <div />
        <div>Associated feature</div>
        <div className="flex justify-between">
          <span>Default Value</span>
          <Tooltip
            body="The default value is the feature value that should be used if no event associated with the entity is found in the given time window. E.g. 0 for transaction count."
            asChild
          >
            <Icon color="text-gray-500" icon={faInfoCircle} size="2xs" />
          </Tooltip>
        </div>
        <div />
        {fields.map((field, index) => (
          <Fragment key={field.id}>
            <div>
              <Controller<
                QueryFormValues,
                `columns.features.${number}.column_name`
              >
                name={`columns.features.${index}.column_name`}
                render={({ field, fieldState, formState }) => (
                  <ColumnNameSelector
                    dataLoc={`feature-column-selector-${index}`}
                    disabled={disabled}
                    errored={
                      !!fieldState.error?.message ||
                      !!formState.errors.columns?.features?.root?.message
                    }
                    options={columnsOptions}
                    previewMutation={previewMutation}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  />
                )}
                rules={{
                  deps: ["columns.features", "columns"],
                }}
              />
            </div>
            <div className="flex items-center">
              <Icon color="text-gray-500" icon={faArrowRight} size="xs" />
            </div>
            <div>
              <FeatureCombobox
                featureOptions={featureOptions}
                featuresLoading={featuresLoading}
                handleFeatureSelection={handleFeatureSelection}
                index={index}
                openModal={openModal}
              />
            </div>
            <div className="flex w-auto flex-shrink items-center">
              <Controller<
                QueryFormValues,
                `columns.features.${number}.feature_default`
              >
                name={`columns.features.${index}.feature_default`}
                render={({ field, fieldState }) => {
                  const selectedFeatureType = form.watch(
                    `columns.features.${index}.feature_type`,
                  );

                  const isDateOrDatetime =
                    selectedFeatureType ===
                      PropertyDefinitionOutputTypeEnum.DATE ||
                    selectedFeatureType ===
                      PropertyDefinitionOutputTypeEnum.DATETIME;

                  return (
                    <Tooltip
                      body="Features of type date or datetime only support default value None."
                      disabled={!isDateOrDatetime}
                      asChild
                    >
                      <div className="w-full">
                        <EditorBoundsProvider maxX={maxX}>
                          <CodeInput
                            dataLoc={`feature-default-${index}`}
                            disabled={disabled || isDateOrDatetime}
                            error={fieldState.error?.message}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                          />
                        </EditorBoundsProvider>
                      </div>
                    </Tooltip>
                  );
                }}
                rules={{
                  deps: ["columns.features", "columns"],
                }}
              />
            </div>
            <div className="flex items-center">
              <Icon
                color={fields.length === 1 ? "text-gray-300" : "text-gray-500"}
                dataLoc="feature-mapping-remove"
                disabled={fields.length === 1 || disabled}
                icon={faTrashAlt}
                size="xs"
                onClick={() => remove(index)}
              />
            </div>
            {form.formState.errors.columns?.features?.[index] && (
              <div className="col-span-full">
                <ErrorHint>
                  {form.formState.errors.columns?.features?.[index]?.column_name
                    ?.message ||
                    form.formState.errors.columns?.features?.[index]
                      ?.feature_key?.message}
                </ErrorHint>
              </div>
            )}
          </Fragment>
        ))}
      </div>
      <div>
        {form.formState.errors.columns?.features?.root?.message && (
          <ErrorHint>
            {form.formState.errors.columns?.features?.root?.message}
          </ErrorHint>
        )}
      </div>
      <div>
        <Button
          dataLoc="add-feature-button"
          disabled={disabled}
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={() =>
            append({
              column_name: "",
              feature_key: "",
              feature_default: "None",
            })
          }
        >
          Add Feature
        </Button>
      </div>
      <ManageFeatureModal
        afterLeave={afterLeave}
        isOpen={isOpen}
        mode="create"
        onClose={closeModal}
        onCreated={(feature) => {
          if (typeof modalData?.index === "number") {
            form.setValue(
              `columns.features.${modalData.index}.feature_key`,
              feature.key,
            );
          }
        }}
      />
    </div>
  );
};

const ToggleableSubSection: React.FC<{
  title: string;
  help: React.ReactNode;
  icon?: IconProp;
  children: React.ReactNode;
  isOpen: boolean;
  dataLoc?: string;
  onToggle: (checked: boolean) => void;
}> = ({ title, icon, help, children, isOpen, onToggle, dataLoc }) => {
  return (
    <div
      className={twJoin(
        "flex flex-col rounded-lg bg-gray-50 p-3",
        isOpen && "gap-3",
      )}
      data-loc={dataLoc}
    >
      <div className="flex items-center gap-x-1 text-gray-800 font-inter-medium-12px">
        {icon && <Icon color="text-gray-800" icon={icon} size="xs" />}
        <span>{title}</span>
        <Tooltip
          body={help}
          footerAction={{
            onClick: noop,
            text: "Learn more",
          }}
          placement="right-start"
          asChild
        >
          <Icon color="text-gray-500" icon={faInfoCircle} size="2xs" />
        </Tooltip>
        <div className="ml-auto">
          <Switch
            dataLoc={`${dataLoc}-section-toggle`}
            enabled={isOpen}
            onChange={onToggle}
          />
        </div>
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <m.div
            animate={{
              height: "auto",
              transition: { duration: 0.2 },
            }}
            className="p-0.5"
            exit={{ height: 0, transition: { duration: 0.2 } }}
            initial={{ height: 0 }}
            style={{ overflow: "hidden" }}
            layout
          >
            {children}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MappingConfiguration: React.FC<{
  previewMutation: UseFeatureQueryPreview;
  previewData: PreviewQueryResponse["data"];
  disabled?: boolean;
  featuresQuery: ReturnType<typeof useFeatures>;
}> = ({ previewMutation, previewData = [], disabled, featuresQuery }) => {
  const { data: featuresResponse, isLoading: featuresLoading } = featuresQuery;

  const { workspace } = useWorkspaceContext();
  const { data: entitiesResponse, isLoading: entitiesLoading } =
    useEntitySchemas({
      baseUrl: workspace.base_url!,
    });

  const columns = useWatch<QueryFormValues, "columns">({
    name: "columns",
  });

  const columnsDataTypes = inferDataTypes(previewData);
  const usedColumns = [
    ...(columns?.entities?.map((entity) => entity.column_name) ?? []),
    ...(columns?.features?.map((feature) => feature.column_name) ?? []),
    ...(columns?.grouping_keys ?? []),
  ].filter(Boolean);

  const columnsOptions = Object.keys(previewData?.at(0) ?? {}).map(
    (column) => ({
      key: column,
      value: (
        <div className="flex items-center gap-x-2">
          <Icon
            color="text-gray-500"
            icon={TYPE_ICONS[columnsDataTypes[column] ?? "any"]}
            size="2xs"
          />
          {column}
        </div>
      ),
      disabled: usedColumns.includes(column)
        ? "This column has already been mapped"
        : undefined,
    }),
  );

  const availableFeatures = featuresResponse?.filter(
    (feature) => !feature.query,
  );

  const form = useFormContext<QueryFormValues>();
  const showEntityMapping = columns?.entities?.length !== 0;
  const showAdditionalGrouping = columns?.grouping_keys?.length !== 0;

  return (
    <>
      <ToggleableSubSection
        help="Choose the primary entity (e.g., Account, Card) this feature will be associated with and map it to the corresponding ID column from your query results."
        icon={faCube}
        isOpen={showEntityMapping}
        title="Entity mapping"
        onToggle={(checked) => {
          if (checked) {
            form.setValue("columns.entities", [
              { column_name: "", entity_name: "" },
            ]);
          } else {
            form.setValue("columns.entities", []);
          }
        }}
      >
        <div className="grid grid-cols-[1fr_20px_1fr] gap-2 text-gray-500 font-inter-normal-12px">
          <div>Select column</div>
          <div></div>
          <div>Associated entity</div>
          <div>
            <Controller
              name="columns.entities.0.column_name"
              render={({ field, fieldState }) => (
                <ColumnNameSelector
                  dataLoc="entity-column-selector"
                  disabled={disabled}
                  errored={!!fieldState.error?.message}
                  options={columnsOptions}
                  previewMutation={previewMutation}
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <div className="flex items-center">
            <Icon color="text-gray-500" icon={faArrowRight} size="xs" />
          </div>
          <div>
            <Controller
              name="columns.entities.0.entity_name"
              render={({ field, fieldState }) => (
                <Combobox
                  dataLoc="entity-selector"
                  disabled={entitiesLoading || disabled}
                  dropdownPlaceholder={
                    <EmptyState
                      description={
                        <>
                          Create a new entity to map
                          <br /> your column to.
                        </>
                      }
                      headline="No entities found"
                      icon={faBuilding}
                      size="sm"
                    />
                  }
                  errored={!!fieldState.error?.message}
                  options={
                    entitiesResponse?.entities?.map((entity) => ({
                      key: entity._id,
                      value: (
                        <div className="flex items-center gap-x-2 text-gray-800">
                          <Icon
                            color="text-gray-500"
                            icon={getSchemaIcon(entity)}
                            size="xs"
                          />
                          {capitalize(entity._display_name_plural)}
                        </div>
                      ),
                    })) ?? []
                  }
                  placeholder={entitiesLoading ? "Loading..." : "Select entity"}
                  value={field.value}
                  showResetButton
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          {form.formState.errors.columns?.entities?.length && (
            <div className="col-span-full">
              <ErrorHint>
                {form.formState.errors.columns?.entities?.[0]?.column_name
                  ?.message ??
                  form.formState.errors.columns?.entities?.[0]?.entity_name
                    ?.message}
              </ErrorHint>
            </div>
          )}
        </div>
      </ToggleableSubSection>
      <ToggleableSubSection
        dataLoc="additional-grouping"
        help={
          <>
            <p className="mb-2">
              Choose additional columns to group feature values (e.g., by
              merchant category, country). Limited to 1000 unique values per
              group.
            </p>

            <p>
              Grouped features can be used like
              <Pill size="sm" variant="dark-gray">
                <Pill.Text>data.entity.feature.group_value</Pill.Text>
              </Pill>
              in the decision flow{" "}
            </p>
          </>
        }
        isOpen={showAdditionalGrouping}
        title="Additional Grouping"
        onToggle={(checked) => {
          if (checked) {
            form.setValue("columns.grouping_keys", [""]);
          } else {
            form.setValue("columns.grouping_keys", []);
          }
        }}
      >
        <div className="flex flex-col gap-2 text-gray-500 font-inter-normal-12px">
          <div>Grouping</div>
          <div>
            <Controller
              name="columns.grouping_keys.0"
              render={({ field, fieldState }) => (
                <ColumnNameSelector
                  dataLoc="grouping-key-selector"
                  disabled={disabled}
                  errored={!!fieldState.error?.message}
                  options={columnsOptions}
                  previewMutation={previewMutation}
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                />
              )}
              rules={{
                deps: ["columns"],
              }}
            />
            {form.formState.errors.columns?.grouping_keys?.[0]?.message && (
              <ErrorHint>
                {form.formState.errors.columns?.grouping_keys?.[0]?.message}
              </ErrorHint>
            )}
          </div>
        </div>
      </ToggleableSubSection>
      <FeatureMapping
        columnsOptions={columnsOptions}
        disabled={disabled}
        featureOptions={
          availableFeatures?.map((feature) => ({
            key: feature.key,
            feature_type: feature.feature_type,
          })) ?? []
        }
        featuresLoading={featuresLoading}
        previewMutation={previewMutation}
      />
    </>
  );
};

const prepareFeatureQuery = (
  data: QueryFormValues,
  previewData: PreviewQueryResponse["data"],
): FeatureQueryCreate | FeatureQueryPut => {
  const types = inferDataTypes(previewData);

  const entities = data.columns.entities?.filter<EntityMapping>(
    (entity): entity is EntityMapping =>
      Boolean(entity.column_name && entity.entity_name),
  );
  const features = data.columns.features?.filter<FeatureMappingT>(
    (feature): feature is FeatureMappingT =>
      Boolean(feature.column_name && feature.feature_key),
  );
  const groupingKeys = data.columns.grouping_keys?.filter(isString);

  return {
    ...data,
    columns: {
      entities,
      grouping_keys: groupingKeys,
      features: features?.map((feature) => ({
        ...feature,
        feature_type: types[feature.column_name],
      })),
    },
  };
};

const isWindowHopOptionDisabled = (
  value: number,
  windowLength: number | null,
) =>
  windowLength
    ? value > windowLength ||
      value < getMinWindowHopFromWindowLength(windowLength)
    : false;

const ColumnNameSelector: React.FC<{
  value: string | undefined;
  options: Option[];
  onChange: (value: Nullable<string>) => void;
  onBlur?: () => void;
  previewMutation: UseFeatureQueryPreview;
  errored: boolean;
  dataLoc: string;
  disabled?: boolean;
}> = ({
  options,
  value,
  onChange,
  onBlur,
  previewMutation,
  errored,
  dataLoc,
  disabled,
}) => {
  const optionValue = options.find((col) => col.key === value);

  // Populate the options with the value if it's not in the options
  // Validation handles error state for fake value
  const updatedOptions =
    !optionValue && value
      ? [
          {
            key: value,
            value: (
              <div className="flex items-center gap-x-2 text-gray-500 line-through">
                <Icon color="text-gray-500" icon={TYPE_ICONS.any} size="2xs" />
                {value}
              </div>
            ),
            disabled: true,
          },
          ...options,
        ]
      : options;

  const isLoading = !value && previewMutation.isPending;

  return (
    <Combobox
      dataLoc={dataLoc}
      disabled={isLoading || disabled}
      dropdownPlaceholder={
        <EmptyState
          action={
            <RunQueryButton
              disabled={disabled}
              featurePreview={previewMutation}
            />
          }
          description="Run query to get column name suggestions."
          headline="No columns found"
          icon={faPlay}
          size="sm"
        />
      }
      errored={errored}
      options={updatedOptions}
      placeholder={isLoading ? "Loading..." : "Select column"}
      value={value ?? null}
      showResetButton
      onBlur={onBlur}
      onChange={(newValue) => {
        // We need onBlur to trigger validation right after selection
        onChange(newValue);
        onBlur?.();
      }}
    />
  );
};

const QueryHasChangedCallout: React.FC<{
  previewMutation: UseFeatureQueryPreview;
}> = ({ previewMutation }) => {
  const queryString = useWatch({ name: "query" });

  if (
    previewMutation.isSuccess &&
    previewMutation.variables?.queryString !== queryString
  ) {
    return (
      <div className="pb-4">
        <Callout type="warning">
          The query has changed, please run the query again
        </Callout>
      </div>
    );
  }

  return null;
};

const ExploreTables: React.FC = () => {
  const { data: eventTypes } = useEventSchemas();

  const [selectedEventType, setSelectedEventType] = useState<string | null>(
    null,
  );

  const { data: previewData, isLoading } =
    useExploreEventTable(selectedEventType);

  useEffect(() => {
    if (!selectedEventType && eventTypes?.length) {
      setSelectedEventType(eventTypes[0].event_type);
    }
  }, [eventTypes, selectedEventType]);

  const noDataFound = isEmpty(previewData);

  const rows = Object.entries(previewData ?? {}).map(
    ([key, value]) =>
      [
        key.includes(".") ? `"${key}"` : key,
        speakPythonPrimitive(objectToSpeakPythonString(value)),
      ] as [string, string],
  );

  return (
    <div className="flex h-full flex-col gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
      <div>
        <div className="mb-2 text-gray-500 font-inter-normal-12px">
          Select event table
        </div>
        <div>
          <Select
            options={
              eventTypes?.map((eventType) => ({
                key: eventType.event_type,
                value: eventType.event_type,
              })) ?? []
            }
            placeholder="Select event table"
            value={selectedEventType}
            onChange={setSelectedEventType}
          />
        </div>
      </div>
      <div className="decideScrollbar min-h-0 flex-1">
        {/* Do not remove wrapping div, breaks visually the table inside */}
        <div>
          {(() => {
            if (isLoading) {
              return <DataList loadingRows={10} borders dividers isLoading />;
            }

            return noDataFound ? (
              <EmptyState
                description="No data found"
                headline="No data found"
                icon={faSearch}
              />
            ) : (
              <DataList rows={rows} borders dividers keyEmphasized />
            );
          })()}
        </div>
      </div>
    </div>
  );
};

type FeatureComboboxProps = {
  index: number;
  featuresLoading: boolean;
  featureOptions: { key: string; feature_type?: string }[];
  openModal: ({ index }: { index: number }) => void;
  handleFeatureSelection: (
    value: Nullable<string>,
    index: number,
    onChange: (value: Nullable<string>) => void,
  ) => void;
};

const FeatureCombobox: React.FC<FeatureComboboxProps> = ({
  index,
  featuresLoading,
  featureOptions,
  openModal,
  handleFeatureSelection,
}) => {
  const { formState, fieldState, field } = useController<
    QueryFormValues,
    `columns.features.${number}.feature_key`
  >({
    name: `columns.features.${index}.feature_key`,
    rules: {
      deps: ["columns.features", "columns"],
    },
  });

  return (
    <Combobox
      dataLoc={`feature-selector-${index}`}
      disabled={featuresLoading || field.disabled}
      dropdownPlaceholder={
        <EmptyState
          description={
            <>
              Create a new feature to map
              <br /> your column to.
            </>
          }
          headline="No features found"
          icon={faDatabase}
          size="sm"
        />
      }
      errored={
        !!fieldState.error?.message ||
        !!formState.errors.columns?.features?.root?.message
      }
      footer={
        <button
          className="flex w-full cursor-pointer gap-x-2 px-4 py-3 text-gray-800 font-inter-normal-13px hover:bg-gray-50"
          type="button"
          onClick={() => openModal({ index })}
        >
          <Icon color="text-indigo-500" icon={faPlus} size="xs" />
          <span>Create new feature</span>
        </button>
      }
      options={featureOptions.map((option) => ({
        ...option,
        value: (
          <Tooltip
            key={option.key}
            body={<div className="max-w-72 break-all">{option.key}</div>}
            placement="right"
            asChild
          >
            <div className="flex items-center gap-x-2 truncate">
              <Icon
                color="text-gray-500"
                icon={getTypeIcon(
                  option.feature_type ?? "",
                  faCalculatorSimple,
                )}
                size="xs"
              />
              <span className="truncate">{option.key}</span>
            </div>
          </Tooltip>
        ),
      }))}
      placeholder={featuresLoading ? "Loading..." : "Select feature"}
      value={field.value}
      showResetButton
      onBlur={field.onBlur}
      onChange={(value) => {
        handleFeatureSelection(value, index, field.onChange);
      }}
    />
  );
};
